import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form } from 'react-bootstrap';
import Button from 'components/Button';
import DatePicker from 'components/DatePicker';
import Toast from 'components/Toast';
import { useMembershipService } from 'services/memberships';
import { useCompanyServiceQueries } from 'services/company';
import { useActiveCompanyStore } from 'stores/activeCompanyStore';
import {
  MembershipGender,
  MEMBERSHIP_DEPARTMENT_MINISTRY_OPTIONS,
} from 'types/membership';
import { ROUTES } from 'utils/constants';
import { formatDateToUTC } from 'utils/dateUtils';
import { ArrowLeft } from 'lucide-react';
import styles from '../Membership.module.scss';
import addStyles from './AddMembership.module.scss';

interface CompanyUser {
  id: string;
  email: string;
  username?: string;
  role?: 'owner' | 'member';
  owned?: boolean;
}

type CreateMode = 'linkUser' | 'inviteEmail';

function AddMembership() {
  const navigate = useNavigate();
  const membershipService = useMembershipService();
  const companyService = useCompanyServiceQueries();
  const activeCompanyId = useActiveCompanyStore((s) => s.activeCompanyId);

  const [mode, setMode] = useState<CreateMode>('inviteEmail');
  const [userId, setUserId] = useState('');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [noMiddleName, setNoMiddleName] = useState(false);
  const [membershipDate, setMembershipDate] = useState(() =>
    formatDateToUTC(new Date()),
  );
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [gender, setGender] = useState('');
  const [civilStatus, setCivilStatus] = useState('');
  const [departmentMinistry, setDepartmentMinistry] = useState('');
  const [nationality, setNationality] = useState('');
  const [addressStreet, setAddressStreet] = useState('');
  const [addressBarangayCity, setAddressBarangayCity] = useState('');
  const [contactNumber, setContactNumber] = useState('');
  const [occupation, setOccupation] = useState('');
  const [employer, setEmployer] = useState('');
  const [membershipType, setMembershipType] = useState('');
  const [isSgl, setIsSgl] = useState(false);
  const [isSgm, setIsSgm] = useState(false);
  const [willingToWorkInTupad, setWillingToWorkInTupad] = useState(false);
  const [status, setStatus] = useState('active');
  const [notes, setNotes] = useState('');
  const [emergencyContactName, setEmergencyContactName] = useState('');
  const [emergencyContactNumber, setEmergencyContactNumber] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [companyUsers, setCompanyUsers] = useState<CompanyUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);

  const loadCompanyUsers = useCallback(async () => {
    if (!activeCompanyId) return;
    setLoadingUsers(true);
    try {
      const users = await companyService.getCompanyUsers(activeCompanyId);
      setCompanyUsers(users);
    } catch {
      setCompanyUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  }, [activeCompanyId, companyService]);

  useEffect(() => {
    if (mode === 'linkUser') {
      void loadCompanyUsers();
    }
  }, [mode, loadCompanyUsers]);

  const showToast = useCallback(
    (message: string, type: 'success' | 'error') => {
      setToast({ message, type });
    },
    [],
  );

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (!firstName.trim()) {
      showToast('First name is required', 'error');
      return;
    }
    if (!lastName.trim()) {
      showToast('Last name is required', 'error');
      return;
    }
    if (!membershipDate.trim()) {
      showToast('Membership date is required', 'error');
      return;
    }

    if (mode === 'linkUser') {
      if (!userId) {
        showToast('Please select a user to link', 'error');
        return;
      }
    } else {
      if (!email.trim()) {
        showToast('Email is required when inviting by email', 'error');
        return;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email.trim())) {
        showToast('Please enter a valid email address', 'error');
        return;
      }
    }

    const membershipDateISO = membershipDate.includes('T')
      ? membershipDate
      : `${membershipDate}T00:00:00.000Z`;
    const dateOfBirthISO =
      dateOfBirth && dateOfBirth.trim()
        ? dateOfBirth.includes('T')
          ? dateOfBirth
          : `${dateOfBirth}T00:00:00.000Z`
        : undefined;

    setIsSubmitting(true);
    try {
      const payload = {
        ...(mode === 'linkUser' ? { userId } : { email: email.trim() }),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        middleName: noMiddleName ? undefined : middleName.trim() || undefined,
        dateOfBirth: dateOfBirthISO,
        gender: (gender as MembershipGender) || undefined,
        civilStatus:
          (civilStatus as
            'single' | 'married' | 'widowed' | 'separated' | 'divorced') ||
          undefined,
        departmentMinistry: departmentMinistry.trim() || undefined,
        nationality: nationality.trim() || undefined,
        address:
          [addressStreet.trim(), addressBarangayCity.trim()]
            .filter(Boolean)
            .join(', ') || undefined,
        contactNumber: contactNumber.trim() || undefined,
        occupation: occupation.trim() || undefined,
        employer: employer.trim() || undefined,
        membershipDate: membershipDateISO,
        membershipType: membershipType.trim() || undefined,
        isSgl,
        isSgm,
        willingToWorkInTupad,
        status: (status as 'active' | 'inactive' | 'suspended') || 'active',
        notes: notes.trim() || undefined,
        emergencyContactName: emergencyContactName.trim() || undefined,
        emergencyContactNumber: emergencyContactNumber.trim() || undefined,
      };

      await membershipService.createMembership(payload);
      navigate(ROUTES.MEMBERSHIP.ROOT, {
        state: {
          toast: {
            message: 'Membership created successfully',
            type: 'success' as const,
          },
        },
      });
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : 'Failed to create membership',
        'error',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={addStyles.container}>
      <header className={addStyles.header}>
        <Button
          type="button"
          variant="ghost"
          onClick={() => navigate(ROUTES.MEMBERSHIP.ROOT)}
          className={addStyles.backButton}
          aria-label="Back to Membership"
        >
          <ArrowLeft size={20} strokeWidth={2} />
          Back
        </Button>
        <h1 className={addStyles.title}>Add Membership</h1>
      </header>

      <div className={addStyles.formCard}>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            void handleSubmit(e);
          }}
        >
          <div className={styles.formGroup}>
            <span className={styles.label}>Create by</span>
            <div className={styles.modeToggle}>
              <button
                type="button"
                className={`${styles.modeButton} ${
                  mode === 'linkUser' ? styles.modeButtonActive : ''
                }`}
                onClick={() => setMode('linkUser')}
              >
                Link existing user
              </button>
              <button
                type="button"
                className={`${styles.modeButton} ${
                  mode === 'inviteEmail' ? styles.modeButtonActive : ''
                }`}
                onClick={() => setMode('inviteEmail')}
              >
                Invite by email
              </button>
            </div>
          </div>

          {mode === 'linkUser' && (
            <Form.Group className={styles.formGroup}>
              <Form.Label className={styles.label}>User *</Form.Label>
              <Form.Select
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                className={styles.select}
                disabled={loadingUsers}
              >
                <option value="">Select a user...</option>
                {companyUsers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.email}
                    {u.username ? ` (${u.username})` : ''}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          )}

          {mode === 'inviteEmail' && (
            <Form.Group className={styles.formGroup}>
              <Form.Label className={styles.label}>Email *</Form.Label>
              <Form.Control
                type="email"
                name="email"
                autoComplete="email"
                placeholder="member@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={styles.input}
              />
            </Form.Group>
          )}

          <Form.Group className={styles.formGroup}>
            <Form.Label className={styles.label}>First name *</Form.Label>
            <Form.Control
              type="text"
              name="firstName"
              autoComplete="given-name"
              placeholder="First name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value.toUpperCase())}
              required
              className={styles.input}
            />
          </Form.Group>

          <Form.Group className={styles.formGroup}>
            <Form.Label className={styles.label}>Last name *</Form.Label>
            <Form.Control
              type="text"
              name="lastName"
              autoComplete="family-name"
              placeholder="Last name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value.toUpperCase())}
              required
              className={styles.input}
            />
          </Form.Group>

          <Form.Group className={styles.formGroup}>
            <Form.Label className={styles.label}>Middle name</Form.Label>
            <Form.Check
              type="checkbox"
              id="noMiddleName"
              label="Member has no middle name"
              checked={noMiddleName}
              onChange={(e) => {
                const checked = e.target.checked;
                setNoMiddleName(checked);
                if (checked) setMiddleName('');
              }}
              className={addStyles.checkbox}
            />
            <Form.Control
              type="text"
              name="middleName"
              autoComplete="additional-name"
              placeholder="Middle name"
              value={middleName}
              onChange={(e) => setMiddleName(e.target.value.toUpperCase())}
              className={styles.input}
              disabled={noMiddleName}
            />
          </Form.Group>

          <Form.Group className={styles.formGroup}>
            <Form.Label className={styles.label}>Membership date *</Form.Label>
            <DatePicker
              value={membershipDate}
              onChange={(v) => setMembershipDate(v ?? '')}
              className={styles.input}
              placeholder="mm/dd/yyyy"
            />
          </Form.Group>

          <Form.Group className={styles.formGroup}>
            <Form.Label className={styles.label}>Date of birth</Form.Label>
            <DatePicker
              name="dateOfBirth"
              value={dateOfBirth}
              onChange={(v) => setDateOfBirth(v ?? '')}
              className={styles.input}
              placeholder="mm/dd/yyyy"
            />
          </Form.Group>

          <Form.Group className={styles.formGroup}>
            <Form.Label className={styles.label}>Gender</Form.Label>
            <Form.Select
              value={gender}
              onChange={(e) => setGender(e.target.value)}
              className={styles.select}
            >
              <option value="">Select...</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className={styles.formGroup}>
            <Form.Label className={styles.label}>Civil status</Form.Label>
            <Form.Select
              value={civilStatus}
              onChange={(e) => setCivilStatus(e.target.value)}
              className={styles.select}
            >
              <option value="">Select...</option>
              <option value="single">Single</option>
              <option value="married">Married</option>
              <option value="widowed">Widowed</option>
              <option value="separated">Separated</option>
              <option value="divorced">Divorced</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className={styles.formGroup}>
            <Form.Label className={styles.label}>
              Department/Ministry
            </Form.Label>
            <Form.Select
              value={departmentMinistry}
              onChange={(e) => setDepartmentMinistry(e.target.value)}
              className={styles.select}
            >
              <option value="">Select...</option>
              {MEMBERSHIP_DEPARTMENT_MINISTRY_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className={styles.formGroup}>
            <Form.Label className={styles.label}>Nationality</Form.Label>
            <Form.Control
              type="text"
              name="nationality"
              autoComplete="country-name"
              placeholder="ex. Filipino"
              value={nationality}
              onChange={(e) => setNationality(e.target.value.toUpperCase())}
              className={styles.input}
            />
          </Form.Group>

          <Form.Group className={styles.formGroup}>
            <Form.Label className={styles.label}>
              Blk. Lot/House number and Street name
            </Form.Label>
            <Form.Control
              type="text"
              name="addressLine1"
              autoComplete="address-line1"
              placeholder="Blk. Lot/House number and Street name"
              value={addressStreet}
              onChange={(e) => setAddressStreet(e.target.value)}
              onBlur={(e) => setAddressStreet(e.target.value.toUpperCase())}
              className={styles.input}
            />
          </Form.Group>

          <Form.Group className={styles.formGroup}>
            <Form.Label className={styles.label}>Barangay and City</Form.Label>
            <Form.Control
              type="text"
              name="addressCity"
              autoComplete="address-level2"
              placeholder="Barangay and City"
              value={addressBarangayCity}
              onChange={(e) => setAddressBarangayCity(e.target.value)}
              onBlur={(e) =>
                setAddressBarangayCity(e.target.value.toUpperCase())
              }
              className={styles.input}
            />
          </Form.Group>

          <Form.Group className={styles.formGroup}>
            <Form.Label className={styles.label}>Contact number</Form.Label>
            <Form.Control
              type="text"
              name="contactNumber"
              autoComplete="tel"
              placeholder="ex. 09123456789"
              value={contactNumber}
              onChange={(e) => setContactNumber(e.target.value)}
              className={styles.input}
            />
          </Form.Group>

          <Form.Group className={styles.formGroup}>
            <Form.Label className={styles.label}>Occupation</Form.Label>
            <Form.Select
              value={occupation}
              onChange={(e) => setOccupation(e.target.value)}
              className={styles.select}
            >
              <option value="">Select...</option>
              <option value="Employed">Employed</option>
              <option value="Self-Employed">Self-Employed</option>
              <option value="Unemployed">Unemployed</option>
              <option value="Student">Student</option>
              <option value="Homemaker (ex. Housewife...)">
                Homemaker (ex. Housewife...)
              </option>
              <option value="Retired">Retired</option>
              <option value="Part-time Worker">Part-time Worker</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className={styles.formGroup}>
            <Form.Label className={styles.label}>
              Employer / Company Name
            </Form.Label>
            <Form.Control
              type="text"
              name="employer"
              autoComplete="organization"
              placeholder="Employer / Company Name"
              value={employer}
              onChange={(e) => setEmployer(e.target.value.toUpperCase())}
              className={styles.input}
            />
          </Form.Group>

          <Form.Group className={styles.formGroup}>
            <Form.Label className={styles.label}>Membership type</Form.Label>
            <Form.Select
              value={membershipType}
              onChange={(e) => setMembershipType(e.target.value)}
              className={styles.select}
            >
              <option value="">Select...</option>
              <option value="Pastor / Minister">Pastor / Minister</option>
              <option value="Board / Council (Administration)">
                Board / Council (Administration)
              </option>
              <option value="Elder / Leader">Elder / Leader</option>
              <option value="Member (at least 1 year of attendance)">
                Member (at least 1 year of attendance)
              </option>
              <option value="Attender (less than 1 year of attendance)">
                Attender (less than 1 year of attendance)
              </option>
            </Form.Select>
            <div className={styles.membershipRoleCheckboxes}>
              <Form.Check
                type="checkbox"
                id="add-membership-is-sgl"
                label="SGL (Small Group Leader)"
                checked={isSgl}
                onChange={(e) => {
                  const nextChecked = e.target.checked;
                  setIsSgl(nextChecked);
                  if (nextChecked) setIsSgm(false);
                }}
                className={styles.membershipRoleCheckbox}
              />
              <Form.Check
                type="checkbox"
                id="add-membership-is-sgm"
                label="SGM (Small Group Member)"
                checked={isSgm}
                onChange={(e) => {
                  const nextChecked = e.target.checked;
                  setIsSgm(nextChecked);
                  if (nextChecked) setIsSgl(false);
                }}
                className={styles.membershipRoleCheckbox}
              />
              <Form.Check
                type="checkbox"
                id="add-membership-willing-to-work-in-tupad"
                label="willing to work in TUPAD"
                checked={willingToWorkInTupad}
                onChange={(e) => setWillingToWorkInTupad(e.target.checked)}
                className={styles.membershipRoleCheckbox}
              />
            </div>
          </Form.Group>

          <Form.Group className={styles.formGroup}>
            <Form.Label className={styles.label}>Status</Form.Label>
            <Form.Select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className={styles.select}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="suspended">Suspended</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className={styles.formGroup}>
            <Form.Label className={styles.label}>
              Emergency contact name
            </Form.Label>
            <Form.Control
              type="text"
              name="emergencyContactName"
              autoComplete="name"
              placeholder="Emergency contact"
              value={emergencyContactName}
              onChange={(e) =>
                setEmergencyContactName(
                  e.target.value.replace(/\d/g, '').toUpperCase(),
                )
              }
              className={styles.input}
            />
          </Form.Group>

          <Form.Group className={styles.formGroup}>
            <Form.Label className={styles.label}>
              Emergency contact number
            </Form.Label>
            <Form.Control
              type="text"
              name="emergencyContactNumber"
              autoComplete="tel"
              placeholder="ex. 09123456789"
              value={emergencyContactNumber}
              onChange={(e) =>
                setEmergencyContactNumber(e.target.value.replace(/\D/g, ''))
              }
              className={styles.input}
            />
          </Form.Group>

          <Form.Group className={styles.formGroup}>
            <Form.Label className={styles.label}>Notes</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Additional notes..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className={styles.textarea}
            />
          </Form.Group>

          <div className={styles.modalFooter}>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(ROUTES.MEMBERSHIP.ROOT)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create Membership'}
            </Button>
          </div>
        </Form>
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}

export default AddMembership;
